"use client"

import React, { useCallback, useState } from 'react'
import { Eye, Loader2, Lock } from 'lucide-react'
import { Button } from '@/shared/components/ui/button'
import { revealPiiField } from '@/features/volunteers/api/pii.api'
import errorToast from '@/shared/lib/toast/error.toast'

/**
 * A PII field that shows a masked value with a "Reveal" button for authorized
 * users. Supports bullet masks (e.g. "•••••1234") and year-only DOB masks.
 * Clicking the button calls the backend reveal endpoint, which is audit-logged.
 *
 * Used for Tier 2 fields (DOB, passport, insurance policy) for users with tier2 access.
 */
export function RevealablePiiField({
    label,
    maskedValue,
    volunteerId,
    fieldName,
    className = '',
}: {
    label: string
    maskedValue: string | null
    volunteerId: number
    fieldName: string
    className?: string
}) {
    const [revealedValue, setRevealedValue] = useState<string | null>(null)
    const [isRevealing, setIsRevealing] = useState(false)

    const handleReveal = useCallback(async () => {
        setIsRevealing(true)
        try {
            const result = await revealPiiField(volunteerId, fieldName)
            if (result.success && result.data) {
                setRevealedValue(result.data.value)
            } else if (!result.success) {
                errorToast(result.message || 'Failed to reveal field')
            } else {
                errorToast('Failed to reveal field')
            }
        } catch {
            errorToast('Failed to reveal field')
        } finally {
            setIsRevealing(false)
        }
    }, [volunteerId, fieldName])

    const displayValue = revealedValue ?? maskedValue

    return (
        <div className={`space-y-0.5 w-full flex flex-col ${className}`}>
            <span className="text-sm font-medium text-foreground">{label}</span>
            <div className="flex items-center gap-2 h-9 min-h-9 px-3 rounded-md border border-input bg-muted/50 text-sm">
                <Lock className="h-3.5 w-3.5 shrink-0 text-muted-foreground/70" />
                <span className={`flex-1 truncate ${revealedValue ? 'text-foreground' : 'text-muted-foreground font-mono'}`}>
                    {displayValue || <span className="italic text-muted-foreground/60">Not provided</span>}
                </span>
                {!revealedValue && maskedValue && (
                    <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={handleReveal}
                        disabled={isRevealing}
                        className="h-6 px-2 text-xs shrink-0 text-primary hover:text-primary/80"
                    >
                        {isRevealing ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                            <>
                                <Eye className="h-3 w-3 mr-1" />
                                Reveal
                            </>
                        )}
                    </Button>
                )}
            </div>
        </div>
    )
}
